import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {useChooseOnlineOfferAlternativeMutation} from '../../../api/onlineOfferApi';
import {OnlineOfferAlternative} from '../../../api/types/onlineOfferAlternativesTypes';
import {getWorkspaceFromUri} from '../../../utils/getWorkspaceFromUri';

const {workspace} = getWorkspaceFromUri();

type ChoiceFailure = 'CHANGED' | 'NOT_SELECTABLE' | 'FAILED';

const ErrorCodeSchema = z.object({
  data: z.object({errors: z.array(z.object({code: z.string()})).min(1)}),
});

const getErrorCode = (error: unknown): string | null =>
  ErrorCodeSchema.safeParse(error).data?.data.errors[0]?.code ?? null;

const getStatus = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error ? error.status : null;

// DMS-BC-OA-0002 = the alternative was revised since the customer saw it; the bare 409 status is
// kept as a fallback for a response without the code.
const getChoiceFailure = (error: unknown): ChoiceFailure =>
  match<{code: string | null; status: unknown}>({
    code: getErrorCode(error),
    status: getStatus(error),
  })
    .with({code: 'DMS-BC-OA-0002'}, {status: 409}, () => 'CHANGED' as const)
    // Withdrawn meanwhile, or no longer selectable.
    .with({code: 'DMS-BC-OA-0001'}, {code: 'DMS-BC-OA-0003'}, () => 'NOT_SELECTABLE' as const)
    .otherwise(() => 'FAILED' as const);

/**
 * Non-binding customer choice: pick → confirm → POST with the revision the customer saw.
 * 409 means the alternative changed meanwhile; the list refetches and the customer confirms again.
 */
export function useChooseAlternative() {
  const params = useParams<{offerId: string}>();
  const [pendingAlternative, setPendingAlternative] = useState<OnlineOfferAlternative | null>(null);
  const [failure, setFailure] = useState<ChoiceFailure | null>(null);
  const [choose, {isLoading}] = useChooseOnlineOfferAlternativeMutation();

  const confirm = () => {
    if (isNil(pendingAlternative)) {
      return;
    }

    setFailure(null);
    choose({
      offerId: params.offerId ?? '',
      workspace: workspace ?? '',
      alternativeId: pendingAlternative.id,
      revision: pendingAlternative.revision,
    })
      .unwrap()
      .then(() => setPendingAlternative(null))
      .catch((error: unknown) => {
        setFailure(getChoiceFailure(error));
        setPendingAlternative(null);
      });
  };

  return {
    pendingAlternative,
    failure,
    isChoosing: isLoading,
    request: (alternative: OnlineOfferAlternative) => {
      // An expired or unavailable alternative cannot be chosen, however the request arrives.
      if (!alternative.selectable) {
        return;
      }
      setFailure(null);
      setPendingAlternative(alternative);
    },
    cancel: () => setPendingAlternative(null),
    confirm,
  };
}
